import { type IconType, Text, ToggleButton } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import type { Control, FieldError, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";

import { FiltersToggleButtonGroup } from "../../ShiftDiscovery/Filters/BottomSheet/ToggleButtonGroup";

interface FilterOption {
  label: string;
  value: string;
  icon?: IconType;
}

interface FilterChipsProps<TFormValues extends FieldValues> {
  name: FieldPath<TFormValues>;
  control: Control<TFormValues>;
  options: FilterOption[];
  title: string;
  /**
   * Delimiter used to separate multiple selected values in the underlying field value.
   * Defaults to a comma (,).
   * Pass a delimiter that does NOT appear inside any option values.
   */
  delimiter?: string;
  allOptions?: boolean;
  /** Show "Any" button for tag-based filters that support showing all options */
  showAnyOption?: boolean;
  /**
   * When true (default), the component behaves as a multi-select allowing any
   * number of chips to be selected. When false, it behaves as a single-select
   * (radio-style) where only one chip can be selected at a time.
   */
  multiSelect?: boolean;
  error?: FieldError;
}

export function FilterChips<TFormValues extends FieldValues>(props: FilterChipsProps<TFormValues>) {
  const {
    name,
    control,
    options,
    title,
    delimiter = ",",
    allOptions = true,
    showAnyOption = false,
    multiSelect = true,
    error,
  } = props;

  return (
    <Stack spacing={5}>
      <Text semibold variant="body1">
        {title}
      </Text>
      <Stack spacing={3}>
        <Stack direction="row" gap={3} flexWrap="wrap">
          <Controller
            name={name}
            control={control}
            // eslint-disable-next-line sonarjs/cognitive-complexity
            render={({ field }) => {
              const values = field.value as string | undefined;
              // Determine currently selected values based on multi-select mode
              const selectedValues: string[] = (() => {
                if (!values) {
                  return [];
                }

                if (multiSelect) {
                  return values.split(delimiter).filter(Boolean);
                }

                return [values];
              })();

              const allValues = options.map((option) => option.value);
              const isAllSelected =
                selectedValues.length === allValues.length &&
                selectedValues.every((value) => allValues.includes(value));
              const isAnySelected = selectedValues.length === 0;

              function handleToggleClick(value: string) {
                if (value === "All") {
                  // Selecting "All" should always result in all options being selected.
                  // Deselecting via "All" is not allowed.
                  if (!isAllSelected) {
                    field.onChange(allValues.join(delimiter));
                  }

                  return;
                }

                if (value === "Any") {
                  // Selecting "Any" clears all selections (empty string)
                  field.onChange("");
                  return;
                }

                if (!multiSelect) {
                  const isSelected = selectedValues.includes(value);

                  if (isSelected) {
                    return;
                  }

                  field.onChange(value);

                  return;
                }

                // If every option is currently selected and the user clicks one of them,
                // deselect all others and keep only the clicked value
                if (isAllSelected && selectedValues.includes(value)) {
                  field.onChange(value);
                  return;
                }

                const isSelected = selectedValues.includes(value);
                // Prevent clearing the last remaining selected chip in multi-select mode.
                if (isSelected && selectedValues.length === 1) {
                  return;
                }

                const newValues = isSelected
                  ? selectedValues.filter((v) => v !== value)
                  : [...selectedValues, value];
                field.onChange(newValues.join(delimiter));
              }

              return (
                <FiltersToggleButtonGroup
                  value={multiSelect ? selectedValues : selectedValues[0]}
                  exclusive={!multiSelect}
                  aria-label={`${title} Filter Toggle Buttons`}
                  onChange={() => {
                    /* We handle state in individual buttons via onClick to retain existing behaviour */
                  }}
                >
                  {allOptions && showAnyOption && (
                    <ToggleButton
                      value="Any"
                      selected={isAnySelected}
                      onClick={() => {
                        handleToggleClick("Any");
                      }}
                    >
                      Any
                    </ToggleButton>
                  )}
                  {allOptions && (
                    <ToggleButton
                      value="All"
                      selected={isAllSelected}
                      onClick={() => {
                        handleToggleClick("All");
                      }}
                    >
                      All
                    </ToggleButton>
                  )}
                  {options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <ToggleButton
                        key={option.value}
                        value={option.value}
                        selected={isSelected || isAllSelected}
                        iconType={option.icon}
                        onClick={() => {
                          handleToggleClick(option.value);
                        }}
                      >
                        {option.label}
                      </ToggleButton>
                    );
                  })}
                </FiltersToggleButtonGroup>
              );
            }}
          />
        </Stack>
        {error && (
          <Text variant="body2" color="error">
            {error.message}
          </Text>
        )}
      </Stack>
    </Stack>
  );
}
